import { useEffect, useRef, useState } from 'preact/hooks';
import {
  expandGroups,
  hasMoreProducts,
  loadingMore,
  loadMore,
  loadMoreError,
  products,
  searchError,
  searchLoading,
  searchParams,
  secondSizeProducts,
  setPage,
  setSort,
  showMrc,
} from '@/store/catalog';
import { addToCart, getCartQty, setCartItem } from '@/store/cart';
import { absoluteImageUrl } from '@/api';
import { formatQuantity, numberFormat } from '@/utils/format';
import { Lightbox } from '@/ui/Lightbox.jsx';
import { QtyWidget } from '@/ui/QtyWidget.jsx';
import { SeasonAll, SeasonSnow, SeasonSun, StudIcon } from '@/ui/Icons.jsx';
import { ProductModal } from './ProductModal.jsx';
import { ProductCard } from './ProductCard.jsx';
import { SkeletonList } from '@/ui/Skeleton.jsx';
import { notify } from '@/store/ui';
import { isMobile } from '@/lib/viewport';
import css from './SearchResults.module.css';

export function SearchResults() {
  const [lightboxSrc, setLightboxSrc] = useState(null);
  const [modalProduct, setModalProduct] = useState(null);

  if (searchLoading.value) {
    return isMobile.value ? (
      <section class="panel">
        <SkeletonList count={6} variant="card" />
      </section>
    ) : (
      <p class="loading">Поиск…</p>
    );
  }
  if (searchError.value) {
    return <section class="panel">Ошибка: {searchError.value.message}</section>;
  }
  if (products.value.length === 0 && secondSizeProducts.value.length === 0) {
    return (
      <section class="panel">
        <p class="empty-state">Ничего не найдено. Попробуйте изменить параметры поиска.</p>
      </section>
    );
  }

  const hideOpt = showMrc.value;
  const onImage = (src) => setLightboxSrc(src);
  const onProduct = (p) => setModalProduct(p);

  return (
    <>
      {secondSizeProducts.value.length > 0 ? (
        <GroupedTable hideOpt={hideOpt} onImage={onImage} onProduct={onProduct} />
      ) : (
        <SimpleTable hideOpt={hideOpt} onImage={onImage} onProduct={onProduct} />
      )}

      {lightboxSrc && <Lightbox src={lightboxSrc} onClose={() => setLightboxSrc(null)} />}
      {modalProduct && <ProductModal sku={modalProduct.sku} onClose={() => setModalProduct(null)} />}
    </>
  );
}

function PriceCells({ product, hideOpt }) {
  const optPrice = product.opt_price ?? product.price;
  return (
    <>
      {!hideOpt && (
        <td>
          <strong>{optPrice > 0 ? `${numberFormat(optPrice)} ₽` : '—'}</strong>
        </td>
      )}
      <td>{product.mrc_price ? `${numberFormat(product.mrc_price)} ₽` : '—'}</td>
    </>
  );
}

function StockBadge({ qty }) {
  return (
    <span class={`${css.badge} ${qty > 0 ? css.inStock : css.outOfStock}`}>
      {formatQuantity(qty)}
    </span>
  );
}

function SeasonCell({ product }) {
  const season = product.specs?.season ?? '';
  const studded = product.specs?.studded ?? false;
  return (
    <>
      {season === 'summer' && <SeasonSun />}
      {season === 'winter' && <SeasonSnow />}
      {season === 'all-season' && <SeasonAll />}
      {studded && <StudIcon />}
    </>
  );
}

function ProductRow({ product, hideOpt, onImage, onProduct }) {
  const inStockQty = product.in_stock_qty ?? product.total_available ?? 0;
  const inTransitQty = product.in_transit_qty ?? 0;
  const totalAvail = inStockQty + inTransitQty;
  const cartQty = getCartQty(product.id);
  const inCart = cartQty > 0;

  function changeQty(qty) {
    if (qty === 0) {
      setCartItem(product, 0);
    } else {
      setCartItem(product, qty);
    }
  }

  function handleAdd() {
    if (inCart) return;
    const qty = Math.max(1, cartQty || 1);
    addToCart(product, qty);
    notify(`${product.name} — ${qty} шт.`);
  }

  function handleRemove() {
    setCartItem(product, 0);
    notify(`${product.name} — убрано из корзины`);
  }

  return (
    <tr>
      <td class={css.tdImg}>
        <button
          type="button"
          class={`${css.iconBtn} ${!product.image_url ? css.iconDim : ''}`}
          title={product.image_url ? 'Посмотреть фото' : 'Нет фото'}
          onClick={() => product.image_url && onImage(absoluteImageUrl(product.image_url))}
          disabled={!product.image_url}
          aria-label="Фото"
        >
          📷
        </button>
      </td>
      <td>
        <a
          href="#"
          onClick={(e) => {
            e.preventDefault();
            onProduct(product);
          }}
        >
          {product.name}
        </a>
      </td>
      <td><SeasonCell product={product} /></td>
      <td>{product.manufacturer ?? '—'}</td>
      <td><StockBadge qty={inStockQty} /></td>
      <td><StockBadge qty={inTransitQty} /></td>
      <PriceCells product={product} hideOpt={hideOpt} />
      <td>
        <QtyWidget value={cartQty} max={totalAvail || 999} disabled={totalAvail <= 0} onChange={changeQty} />
      </td>
      <td>
        {inCart ? (
          <button class="btn btn-success btn-sm" type="button" onClick={handleRemove}>
            В корзине ✕
          </button>
        ) : (
          <button
            class="btn btn-primary btn-sm"
            type="button"
            disabled={totalAvail <= 0}
            onClick={handleAdd}
          >
            В корзину
          </button>
        )}
      </td>
    </tr>
  );
}

function SimpleTable({ hideOpt, onImage, onProduct }) {
  const params = searchParams.value;
  const totalPages = Math.ceil(params.total / params.perPage);

  // Мобильный режим — карточки-«recyclerview» прямо на фоне (без обёртки-панели)
  // + бесшовная подгрузка при скролле вместо кнопок страниц.
  if (isMobile.value) {
    return (
      <div class={css.mobileResults}>
        <p class={css.foundCount}>
          Найдено товаров: <strong>{params.total}</strong>
        </p>
        <div class={css.cardList}>
          {products.value.map((p) => (
            <ProductCard key={p.id} product={p} hideOpt={hideOpt} onImage={onImage} onProduct={onProduct} />
          ))}
        </div>
        <InfiniteSentinel />
      </div>
    );
  }

  return (
    <section class="panel">
      <div class={css.header}>
        <p>
          Найдено товаров: <strong>{params.total}</strong>
        </p>
      </div>
      <div class={css.tableWrap}>
        <table class={css.table}>
          <thead>
            <tr>
              <th />
              <SortableTh field="name" current={params}>
                Название
              </SortableTh>
              <th />
              <th>Производитель</th>
              <th>В наличии</th>
              <th>В пути</th>
              {!hideOpt && (
                <SortableTh field="price" current={params}>
                  ОПТ
                </SortableTh>
              )}
              <SortableTh field="mrc_price" current={params}>
                МРЦ
              </SortableTh>
              <th>Кол-во</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {products.value.map((p) => (
              <ProductRow key={p.id} product={p} hideOpt={hideOpt} onImage={onImage} onProduct={onProduct} />
            ))}
          </tbody>
        </table>
      </div>
      {totalPages > 1 && <Pagination current={params.page} total={totalPages} />}
    </section>
  );
}

function SortableTh({ field, current, children }) {
  const arrow =
    current.sortField === field ? (current.sortDir === 'asc' ? ' ▲' : ' ▼') : '';
  return (
    <th
      class={css.sortable}
      onClick={() => setSort(field)}
      role="columnheader"
      aria-sort={current.sortField === field ? (current.sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      {children}
      {arrow}
    </th>
  );
}

function Pagination({ current, total }) {
  const start = Math.max(1, current - 2);
  const end = Math.min(total, current + 2);
  const pages = [];
  for (let i = start; i <= end; i += 1) pages.push(i);

  return (
    <nav class={css.pagination} aria-label="Пагинация">
      <button type="button" disabled={current <= 1} onClick={() => setPage(current - 1)}>
        «
      </button>
      {pages.map((p) => (
        <button
          key={p}
          type="button"
          class={p === current ? css.activePage : ''}
          onClick={() => setPage(p)}
          aria-current={p === current ? 'page' : undefined}
        >
          {p}
        </button>
      ))}
      <button type="button" disabled={current >= total} onClick={() => setPage(current + 1)}>
        »
      </button>
    </nav>
  );
}

/**
 * Невидимый «маяк» в конце списка: когда попадает во вьюпорт — догружает
 * следующую страницу. Бесшовная пагинация, как в нативных списках.
 */
function InfiniteSentinel() {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        // Не дёргаем подгрузку, пока предыдущая попытка завершилась ошибкой —
        // ждём явного «Повторить», иначе зациклимся на падающем запросе.
        if (entries[0]?.isIntersecting && !loadMoreError.value) loadMore();
      },
      { rootMargin: '400px 0px' }, // подгружаем заранее, до достижения низа
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const more = hasMoreProducts();
  return (
    <div ref={ref} class={css.sentinel}>
      {loadingMore.value && <span class={css.spinner} />}
      {loadMoreError.value && !loadingMore.value && (
        <button type="button" class={css.retryBtn} onClick={() => loadMore()}>
          Не удалось загрузить. Повторить
        </button>
      )}
      {!more && !loadMoreError.value && products.value.length > 0 && (
        <span class={css.endText}>Это всё</span>
      )}
    </div>
  );
}

function GroupedTable({ hideOpt, onImage, onProduct }) {
  const expand = expandGroups.value;
  const allItems = [
    ...products.value.map((p, i) => ({ ...p, _sizeGroup: 1, _origIndex: i })),
    ...secondSizeProducts.value.map((p, i) => ({ ...p, _sizeGroup: 2, _origIndex: i })),
  ];
  const groupMap = new Map();
  for (const p of allItems) {
    const key = (p.manufacturer ?? '—').toLowerCase();
    if (!groupMap.has(key)) groupMap.set(key, { manufacturer: p.manufacturer ?? '—', items: [] });
    groupMap.get(key).items.push(p);
  }
  const groups = [...groupMap.values()]
    .filter((g) => g.items.some((p) => p._sizeGroup === 1) && g.items.some((p) => p._sizeGroup === 2))
    .sort((a, b) => a.manufacturer.localeCompare(b.manufacturer));

  const [openGroups, setOpenGroups] = useState(() => new Set(groups.map((_, i) => i)));

  function toggle(idx) {
    setOpenGroups((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  }

  // Мобильный режим — группы карточек.
  if (isMobile.value) {
    return (
      <section class="panel">
        <div class={css.header}>
          <p>
            Найдено пар: <strong>{groups.length}</strong>
          </p>
        </div>
        {groups.map((g, gi) => {
          const totalStock = g.items.reduce((s, p) => s + (p.in_stock_qty ?? p.total_available ?? 0), 0);
          const isOpen = (expand && !openGroups.size) || openGroups.has(gi);
          return (
            <div key={`mg-${gi}`} class={css.mGroup}>
              <button type="button" class={css.mGroupHead} onClick={() => toggle(gi)}>
                <span class={css.expandBtn}>{isOpen ? '−' : '+'}</span>
                <span class={css.mGroupName}>{g.manufacturer}</span>
                <span class={`${css.badge} ${totalStock > 0 ? css.inStock : css.outOfStock}`}>
                  {formatQuantity(totalStock)}
                </span>
              </button>
              {isOpen && (
                <div class={css.cardList}>
                  {g.items.map((p) => (
                    <ProductCard
                      key={`${gi}-${p.id}-${p._sizeGroup}`}
                      product={p}
                      hideOpt={hideOpt}
                      onImage={onImage}
                      onProduct={onProduct}
                    />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </section>
    );
  }

  return (
    <section class="panel">
      <div class={css.header}>
        <p>
          Найдено пар: <strong>{groups.length}</strong>
        </p>
      </div>
      <div class={css.tableWrap}>
        <table class={css.table}>
          <thead>
            <tr>
              <th style={{ width: 30 }} />
              <th>Название</th>
              <th />
              <th>Производитель</th>
              <th>В наличии</th>
              <th>В пути</th>
              {!hideOpt && <th>ОПТ</th>}
              <th>МРЦ</th>
              <th>Кол-во</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {groups.map((g, gi) => {
              const totalStock = g.items.reduce((s, p) => s + (p.in_stock_qty ?? p.total_available ?? 0), 0);
              const totalTransit = g.items.reduce((s, p) => s + (p.in_transit_qty ?? 0), 0);
              const isOpen = (expand && !openGroups.size) || openGroups.has(gi);
              return (
                <>
                  <tr key={`g-${gi}`} class={css.groupHeader} onClick={() => toggle(gi)}>
                    <td><span class={css.expandBtn}>{isOpen ? '−' : '+'}</span></td>
                    <td>
                      <span>
                        Размер 1: <strong>{g.items.filter((p) => p._sizeGroup === 1).length}</strong> шт. / Размер
                        2: <strong>{g.items.filter((p) => p._sizeGroup === 2).length}</strong> шт.
                      </span>
                    </td>
                    <td />
                    <td><strong>{g.manufacturer}</strong></td>
                    <td><StockBadge qty={totalStock} /></td>
                    <td><StockBadge qty={totalTransit} /></td>
                    {!hideOpt && <td />}
                    <td />
                    <td />
                    <td />
                  </tr>
                  {isOpen &&
                    g.items.map((p) => (
                      <ProductRow
                        key={`${gi}-${p.id}-${p._sizeGroup}`}
                        product={p}
                        hideOpt={hideOpt}
                        onImage={onImage}
                        onProduct={onProduct}
                      />
                    ))}
                </>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}
